import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const CourseContext = createContext();

/** Enrolment only reaches the server for a signed-in user. */
const isSignedIn = () =>
  Boolean(localStorage.getItem('token') || sessionStorage.getItem('token'));

export function CourseProvider({ children }) {
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [courseProgress, setCourseProgress] = useState({});
  const [completedCourses, setCompletedCourses] = useState([]);
  // True until enrolments have been read back from the server. Consumers must
  // wait for this before deciding a user is "not enrolled" — otherwise they
  // act on an empty list during the first render and bounce the user out.
  const [hydrating, setHydrating] = useState(true);

  // Load from localStorage on app start
  useEffect(() => {
    try {
      const savedEnrollments = localStorage.getItem('bytegurukul_enrollments');
      const savedProgress = localStorage.getItem('bytegurukul_course_progress');
      const savedCompleted = localStorage.getItem('bytegurukul_completed_courses');

      if (savedEnrollments) setEnrolledCourses(JSON.parse(savedEnrollments));
      if (savedProgress) setCourseProgress(JSON.parse(savedProgress));
      if (savedCompleted) setCompletedCourses(JSON.parse(savedCompleted));
    } catch (error) {
      console.error('Error loading course data from localStorage:', error);
      // Clear corrupted data
      localStorage.removeItem('bytegurukul_enrollments');
      localStorage.removeItem('bytegurukul_course_progress');
      localStorage.removeItem('bytegurukul_completed_courses');
    }

    // Signed in: the server is authoritative. Enrolment used to live only in
    // this browser, so it vanished on another device and the backend never
    // knew the student had enrolled — which also blocked certificates.
    if (isSignedIn()) {
      api.get('/courses/learning/user/enrolled')
        .then(({ data }) => {
          const rows = data?.data || [];
          if (rows.length === 0) return;
          const fromServer = rows.map((r) => ({
            id: String(r.id),
            name: r.title,
            slug: r.slug,
            thumbnail: r.thumbnailUrl,
            progress: Math.round(Number(r.progressPercentage) || 0),
            enrolledDate: r.lastAccessedAt,
          }));
          setEnrolledCourses((prev) => {
            const seen = new Set(fromServer.map((c) => c.id));
            return [...fromServer, ...prev.filter((c) => !seen.has(String(c.id)))];
          });
          setCourseProgress((prev) => {
            const next = { ...prev };
            fromServer.forEach((c) => { next[c.id] = c.progress; });
            return next;
          });
        })
        .catch(() => { /* offline: keep whatever this browser had */ })
        .finally(() => setHydrating(false));
    } else {
      setHydrating(false);
    }
  }, []);

  // Save to localStorage when data changes
  useEffect(() => {
    localStorage.setItem('bytegurukul_enrollments', JSON.stringify(enrolledCourses));
  }, [enrolledCourses]);

  useEffect(() => {
    localStorage.setItem('bytegurukul_course_progress', JSON.stringify(courseProgress));
  }, [courseProgress]);

  useEffect(() => {
    localStorage.setItem('bytegurukul_completed_courses', JSON.stringify(completedCourses));
  }, [completedCourses]);

  // Enroll in a course.
  //
  // Updates local state immediately so the UI responds, then records the
  // enrolment on the server. Returns a promise so callers can await the
  // real result and surface a failure instead of showing a false success.
  const enrollInCourse = async (course) => {
    if (!course) return { success: false, message: 'No course supplied.' };

    const normalizedId = String(course.id);

    setEnrolledCourses(prev => {
      const alreadyEnrolled = prev.find(c => String(c.id) === normalizedId);
      if (alreadyEnrolled) return prev;

      const newEnrollment = {
        ...course,
        id: normalizedId,
        enrolledDate: new Date().toISOString(),
        progress: 0,
        currentModule: 0,
        currentLesson: 0
      };

      return [...prev, newEnrollment];
    });

    if (!isSignedIn()) {
      return { success: true, local: true };
    }

    try {
      await api.post(`/courses/learning/${course.id}/enroll`);
      return { success: true };
    } catch (err) {
      // Roll the optimistic entry back so the UI doesn't claim an enrolment
      // the server rejected.
      setEnrolledCourses(prev => prev.filter(c => String(c.id) !== normalizedId));
      return {
        success: false,
        message: err.response?.data?.message || 'Could not enrol. Please try again.',
      };
    }
  };

  // Update course progress
  const updateCourseProgress = (courseId, progress, currentModule = 0, currentLesson = 0) => {
    setCourseProgress(prev => ({
      ...prev,
      [courseId]: { progress, currentModule, currentLesson, lastUpdated: new Date().toISOString() }
    }));

    // Update enrolled courses progress
    setEnrolledCourses(prev => 
      prev.map(course => 
        course.id === courseId 
          ? { ...course, progress, currentModule, currentLesson }
          : course
      )
    );

    // Mark as completed if progress is 100%
    if (progress === 100) {
      setCompletedCourses(prev => {
        if (!prev.includes(courseId)) {
          return [...prev, courseId];
        }
        return prev;
      });
    }
  };

  // Check if user is enrolled in a course
  const isEnrolled = (courseId) => {
    return enrolledCourses.some(course => String(course.id) === String(courseId));
  };

  // Get course progress
  // Normalises both shapes. Server hydration stores a bare percentage while
  // the legacy learning page stores { progress, currentModule, currentLesson },
  // and a stored 0 is falsy — so an untouched course would otherwise fall
  // through to the default object and lose its position.
  const getCourseProgress = (courseId) => {
    const raw = courseProgress[courseId];
    if (typeof raw === 'number') {
      return { progress: raw, currentModule: 0, currentLesson: 0 };
    }
    return raw ?? { progress: 0, currentModule: 0, currentLesson: 0 };
  };

  /** Just the percentage, for cards and progress bars. */
  const getCoursePercent = (courseId) => {
    const raw = courseProgress[courseId];
    if (typeof raw === 'number') return raw;
    if (raw && typeof raw.progress === 'number') return raw.progress;
    const enrolled = enrolledCourses.find((c) => String(c.id) === String(courseId));
    return Number(enrolled?.progress) || 0;
  };

  // Get enrolled course by ID
  const getEnrolledCourse = (courseId) => {
    return enrolledCourses.find(course => String(course.id) === String(courseId));
  };

  // Calculate overall learning progress
  const getOverallProgress = () => {
    if (enrolledCourses.length === 0) return 0;
    const totalProgress = enrolledCourses.reduce((sum, course) => sum + course.progress, 0);
    return Math.round(totalProgress / enrolledCourses.length);
  };

  const value = {
    enrolledCourses,
    courseProgress,
    completedCourses,
    hydrating,
    enrollInCourse,
    updateCourseProgress,
    isEnrolled,
    getCourseProgress,
    getCoursePercent,
    getEnrolledCourse,
    getOverallProgress
  };

  return (
    <CourseContext.Provider value={value}>
      {children}
    </CourseContext.Provider>
  );
}

// Custom hook to use course context
export function useCourse() {
  const context = useContext(CourseContext);
  if (!context) {
    throw new Error('useCourse must be used within a CourseProvider');
  }
  return context;
}